import React, { useMemo, useState } from "react";
import { Archive, MoreHorizontal, Pin, Pencil } from "lucide-react";
import {
  parseBluScopeBriefMarkdown,
  parseBluScopeOptionsMarkdown,
  isBluScopeOptionsMessage,
} from "../../blu/parseBluFinalize";
import PacketActions from "../admin/PacketActions";

const chipCls =
  "shrink-0 rounded-full px-3 py-1.5 text-[11px] uppercase tracking-[0.12em] transition-colors disabled:opacity-40";

export function BluChip({ children, onClick, active, accent, disabled, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`${chipCls} ${
        active
          ? "bg-[var(--t-text)] text-white"
          : accent
            ? "bg-[rgba(0,30,255,0.1)] text-[var(--t-accent)] hover:bg-[rgba(0,30,255,0.16)]"
            : "bg-black/[0.04] text-[var(--t-meta)] hover:text-[var(--t-text)]"
      }`}
    >
      {children}
    </button>
  );
}

/** Context strip under bubble header / above chat. */
export function BluContextStrip({ clientName, loadPhase, threadTitle, hasPreview }) {
  const status =
    loadPhase === "notion"
      ? "Pulling notes"
      : loadPhase === "thinking"
        ? "Drafting"
        : loadPhase === "scope"
          ? "Building scope items"
          : loadPhase === "ready"
            ? "Ready"
            : null;

  return (
    <div
      className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-[var(--t-border)]/35 px-3 py-2 text-[10px] uppercase tracking-[0.12em] text-[var(--t-meta)]"
      data-testid="blu-context-strip"
    >
      <span className="truncate">
        {clientName ? (
          <>
            <span className="text-[var(--t-accent)]">Client</span> {clientName}
          </>
        ) : (
          "No client linked"
        )}
      </span>
      {threadTitle ? (
        <>
          <span className="opacity-40">·</span>
          <span className="truncate normal-case tracking-normal">{threadTitle}</span>
        </>
      ) : null}
      {status ? (
        <>
          <span className="opacity-40">·</span>
          <span className="text-[var(--t-accent)]">{status}</span>
        </>
      ) : null}
      {hasPreview ? (
        <>
          <span className="opacity-40">·</span>
          <span>Preview ready</span>
        </>
      ) : null}
    </div>
  );
}

const PROSPECT_STAGES = new Set(["outreach", "met", "proposal", "invoice"]);

/** Quick starter chips above composer. */
export function BluQuickStarters({ clients, selectedClient, onPick, disabled }) {
  const name = selectedClient?.name;
  const isProspect = PROSPECT_STAGES.has(String(selectedClient?.engagement_stage || "").toLowerCase());
  const starters = [
    {
      id: "scope",
      label: name ? `Scope ${name}` : "Scope a client",
      text: name
        ? isProspect
          ? `Let's scope ${name}. Review their prospect meeting notes and record notes, use the standard Scope of Support template, and draft a Step 1 brief. I'll add directing context in chat.`
          : `Let's scope ${name}. Pull portal notes and draft a Step 1 brief.`
        : "Let's scope a client or prospect — ask me which one if unclear.",
    },
    {
      id: "options",
      label: "Scope options",
      text: name
        ? `I want 3 scope options for ${name}. I'll list each option with offering type, investment, and what it entails — draft ## SCOPE OPTIONS from my packages (use their notes for detail/voice). Don't invent tiers or prices.`
        : "I want 3 scope options. I'll list each option with offering type, investment, and what it entails — draft ## SCOPE OPTIONS from my packages. Don't invent tiers or prices.",
    },
    {
      id: "notes",
      label: "Pull notes",
      text: name
        ? isProspect
          ? `Pull prospect meeting notes and record notes for ${name}.`
          : `Pull Overview notes and Notion context for ${name}.`
        : "Pull notes for the client or prospect we're discussing.",
    },
    {
      id: "invoice",
      label: "Draft invoice",
      text: name
        ? `Draft the invoice package for ${name} after the brief looks right.`
        : "Draft an invoice once the scope brief is approved.",
    },
    {
      id: "revise",
      label: "Revise investment",
      text: name
        ? `Revise the investment for ${name} — I'll suggest a new total.`
        : "Revise the investment on the current brief.",
    },
  ];

  return (
    <div className="mb-2 flex gap-1.5 overflow-x-auto pb-0.5 [-webkit-overflow-scrolling:touch]" data-testid="blu-quick-starters">
      {starters.map((s) => (
        <BluChip key={s.id} disabled={disabled} onClick={() => onPick(s.text)} testId={`blu-starter-${s.id}`}>
          {s.label}
        </BluChip>
      ))}
      {clients?.length ? (
        <BluChip disabled={disabled} onClick={() => onPick("__picker__")} testId="blu-starter-client">
          {name ? "Switch client" : "Pick client"}
        </BluChip>
      ) : null}
    </div>
  );
}

/** Compact client picker popover panel. */
export function BluClientPicker({ clients, selectedId, onSelect, onClose }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const list = clients || [];
    const query = q.trim().toLowerCase();
    if (!query) return list.slice(0, 40);
    return list.filter((c) => String(c.name || "").toLowerCase().includes(query)).slice(0, 40);
  }, [clients, q]);

  return (
    <div
      className="mb-2 rounded-xl border border-[var(--t-border)]/50 bg-white p-2 shadow-sm"
      data-testid="blu-client-picker"
    >
      <div className="mb-2 flex items-center justify-between gap-2 px-1">
        <div className="label-eyebrow mb-0">Clients</div>
        <button
          type="button"
          onClick={onClose}
          className="text-[10px] uppercase tracking-[0.14em] text-[var(--t-meta)]"
        >
          Close
        </button>
      </div>
      <input
        className="t-input mb-2 !h-9 w-full !rounded-lg !bg-[var(--t-bg)] !px-3 text-[12px]"
        placeholder="Search…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        data-testid="blu-client-search"
      />
      <div className="t-scroll-soft max-h-40 space-y-0.5 overflow-y-auto">
        {filtered.length === 0 ? (
          <p className="px-2 py-3 text-[12px] text-[var(--t-meta)]">No matches.</p>
        ) : (
          filtered.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => onSelect(c)}
              className={`flex w-full rounded-lg px-2.5 py-2 text-left text-[12px] ${
                selectedId === c.id
                  ? "bg-[rgba(0,30,255,0.08)] text-[var(--t-accent)]"
                  : "text-[var(--t-text)] hover:bg-black/[0.03]"
              }`}
              data-testid={`blu-client-option-${c.id}`}
            >
              {c.name}
            </button>
          ))
        )}
      </div>
    </div>
  );
}

/** Step 1 approve / adjust chips. */
export function BluBriefActions({ disabled, onApprove, onAdjustScope, onAdjustInvestment }) {
  return (
    <div className="mt-2 flex flex-wrap gap-1.5" data-testid="blu-brief-actions">
      <BluChip accent disabled={disabled} onClick={onApprove} testId="blu-approve-brief">
        Looks good
      </BluChip>
      <BluChip disabled={disabled} onClick={onAdjustScope} testId="blu-adjust-scope">
        Adjust scope
      </BluChip>
      <BluChip disabled={disabled} onClick={onAdjustInvestment} testId="blu-adjust-investment">
        Change investment
      </BluChip>
    </div>
  );
}

/** Chips to share all packages or pick one from a ## SCOPE OPTIONS menu. */
export function BluScopeOptionActions({ content, clients, clientName, disabled, onPick }) {
  const parsed = useMemo(
    () => parseBluScopeOptionsMarkdown(content, { clients }),
    [content, clients],
  );
  if (!parsed?.options?.length) return null;
  const name = (parsed.client_name || clientName || "").trim();
  const who = name || "this client";

  return (
    <div className="mt-2 flex flex-wrap gap-1.5" data-testid="blu-scope-option-actions">
      <BluChip
        accent
        disabled={disabled}
        testId="blu-build-all-options"
        onClick={() =>
          onPick?.(
            `Build all options into shareable scope packets for ${who}. One blu-finalize JSON per option with option_label so I can save each PDF to Library and email them.`,
          )
        }
      >
        Save all for sharing
      </BluChip>
      {parsed.options.map((opt) => (
        <BluChip
          key={opt.id}
          disabled={disabled}
          testId={`blu-build-option-${opt.id}`}
          onClick={() => {
            onPick?.(
              `Build Option ${opt.letter} (${opt.shortLabel}) into a full Step 1 brief for ${who}.`,
            );
          }}
        >
          Build {opt.shortLabel}
          {opt.investment
            ? ` · $${Number(opt.investment).toLocaleString()}`
            : ""}
        </BluChip>
      ))}
    </div>
  );
}

/** Editable scope item cards from Step 1 brief. */
export function BluScopeCards({ content, clients, disabled, onRebuild }) {
  const brief = useMemo(() => parseBluScopeBriefMarkdown(content, { clients }), [content, clients]);
  const [items, setItems] = useState(null);
  const [investment, setInvestment] = useState(null);

  const liveItems = items ?? brief?.scope_items ?? [];
  const liveInvestment = investment ?? brief?.investment_total ?? 0;

  if (!brief) return null;

  const updateItem = (idx, patch) => {
    const base = items ?? brief.scope_items.map((it) => ({ ...it }));
    const next = base.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    setItems(next);
  };

  const removeItem = (idx) => {
    const base = items ?? brief.scope_items.map((it) => ({ ...it }));
    setItems(base.filter((_, i) => i !== idx));
  };

  const rebuild = () => {
    const lines = liveItems
      .filter((it) => (it.title || "").trim())
      .map((it, i) => {
        const n = String(i + 1).padStart(2, "0");
        const body = (it.description || "").trim();
        return body ? `${n} — ${it.title.trim()}\n${body}` : `${n} — ${it.title.trim()}`;
      });
    const msg = [
      `Rebuild the Step 1 brief for ${brief.client_name} with this revised scope:`,
      "",
      ...lines,
      "",
      `Investment total: $${Number(liveInvestment || 0).toLocaleString()}`,
      brief.date_range ? `Date range: ${brief.date_range}` : null,
      "Keep TAĪSTU voice and the mandatory Step 1 layout.",
    ]
      .filter(Boolean)
      .join("\n");
    onRebuild?.(msg);
  };

  return (
    <div className="mt-2 space-y-2" data-testid="blu-scope-cards">
      <div className="label-eyebrow">Scope items · editable</div>
      {liveItems.map((it, idx) => (
        <div
          key={`${it.number}-${idx}`}
          className="rounded-xl border border-[var(--t-border)]/45 bg-white p-3"
          data-testid={`blu-scope-card-${idx}`}
        >
          <div className="mb-2 flex items-start justify-between gap-2">
            <input
              className="t-input !h-9 min-w-0 flex-1 !rounded-lg !bg-[var(--t-bg)] !px-3 text-[12px] font-medium"
              value={it.title || ""}
              disabled={disabled}
              onChange={(e) => updateItem(idx, { title: e.target.value })}
              aria-label={`Scope item ${idx + 1} title`}
            />
            <button
              type="button"
              disabled={disabled}
              onClick={() => removeItem(idx)}
              className="shrink-0 text-[10px] uppercase tracking-[0.14em] text-[var(--t-error)]"
            >
              Strike
            </button>
          </div>
          <textarea
            className="t-textarea min-h-[64px] w-full !rounded-lg !bg-[var(--t-bg)] !px-3 !py-2 text-[12px]"
            value={it.description || ""}
            disabled={disabled}
            onChange={(e) => updateItem(idx, { description: e.target.value })}
            aria-label={`Scope item ${idx + 1} description`}
          />
        </div>
      ))}
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-[7rem]">
          <label className="label-eyebrow mb-1 block">Investment $</label>
          <input
            type="number"
            min="0"
            className="t-input !h-9 w-full !rounded-lg !bg-white !px-3 text-[12px]"
            value={liveInvestment || ""}
            disabled={disabled}
            onChange={(e) => setInvestment(e.target.value === "" ? 0 : Number(e.target.value))}
            data-testid="blu-scope-investment"
          />
        </div>
        <BluChip accent disabled={disabled} onClick={rebuild} testId="blu-rebuild-scope">
          Rebuild from this
        </BluChip>
      </div>
    </div>
  );
}

/** Sticky preview dock when finalize payload exists. */
export function BluPreviewDock({
  payload,
  payloads,
  packetFileId,
  packetInvoiceNumber,
  packetByKey,
  busy,
  onFinalize,
  onFinalizeAt,
  onFinalizeAll,
  onScrollToPreview,
}) {
  const list = Array.isArray(payloads) && payloads.length > 1
    ? payloads
    : payload
      ? [payload]
      : [];
  if (!list.length) return null;

  if (list.length === 1) {
    const one = list[0];
    return (
      <div
        className="shrink-0 border-t border-[var(--t-border)]/40 bg-white/90 px-3 py-2.5"
        data-testid="blu-preview-dock"
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="label-eyebrow mb-0.5">Packet</div>
            <div className="truncate text-[12px] text-[var(--t-text)]">
              {one.client_name}
              {one.option_label ? ` · ${one.option_label}` : ""}
              {packetInvoiceNumber ? ` · ${packetInvoiceNumber}` : ""}
              {one.scope_only
                ? " · Scope only"
                : one.investment_total != null
                  ? ` · $${Number(one.investment_total).toLocaleString()}`
                  : ""}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {onScrollToPreview ? (
              <button
                type="button"
                onClick={onScrollToPreview}
                className="text-[10px] uppercase tracking-[0.14em] text-[var(--t-meta)] hover:text-[var(--t-accent)]"
              >
                Open
              </button>
            ) : null}
            {packetFileId ? (
              <PacketActions
                fileId={packetFileId}
                clientId={one.client_id || packetByKey?.["0"]?.clientId || null}
                size="sm"
              />
            ) : (
              <button
                type="button"
                disabled={busy}
                onClick={onFinalize}
                className="neo-cta !min-h-8 !px-3 text-[10px]"
                data-testid="blu-dock-create-packet"
              >
                {busy ? "Creating…" : "Save to Library"}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const allSaved = list.every((_, idx) => packetByKey?.[String(idx)]?.fileId);

  return (
    <div
      className="shrink-0 border-t border-[var(--t-border)]/40 bg-white/90 px-3 py-2.5"
      data-testid="blu-preview-dock-multi"
    >
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="label-eyebrow mb-0.5">Shareable scopes</div>
          <div className="text-[12px] text-[var(--t-text)]">
            {list[0].client_name} · {list.length} packets
          </div>
        </div>
        {!allSaved ? (
          <button
            type="button"
            disabled={busy}
            onClick={onFinalizeAll}
            className="neo-cta !min-h-8 !px-3 text-[10px]"
            data-testid="blu-dock-save-all-packets"
          >
            {busy ? "Saving…" : "Save all to Library"}
          </button>
        ) : null}
      </div>
      <div className="space-y-2">
        {list.map((p, idx) => {
          const key = String(idx);
          const saved = packetByKey?.[key];
          return (
            <div
              key={key}
              className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[var(--t-border)]/40 bg-[var(--t-bg)]/60 px-2.5 py-2"
              data-testid={`blu-dock-option-${idx}`}
            >
              <div className="min-w-0 text-[11px] text-[var(--t-text)]">
                <span className="font-medium">
                  {p.option_label || `Option ${idx + 1}`}
                </span>
                {p.scope_only
                  ? " · Scope only"
                  : p.investment_total != null
                    ? ` · $${Number(p.investment_total).toLocaleString()}`
                    : ""}
                {saved?.invoiceNumber ? ` · ${saved.invoiceNumber}` : ""}
              </div>
              {saved?.fileId ? (
                <PacketActions
                  fileId={saved.fileId}
                  clientId={p?.client_id || saved?.clientId || null}
                  size="sm"
                />
              ) : (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onFinalizeAt?.(idx)}
                  className="text-[10px] uppercase tracking-[0.14em] text-[var(--t-accent)]"
                  data-testid={`blu-dock-save-option-${idx}`}
                >
                  Save
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Thread row overflow menu. */
export function BluThreadMenu({ thread, onRename, onTogglePin, onToggleArchive }) {
  const [open, setOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [title, setTitle] = useState(thread?.title || "");

  if (!thread) return null;

  return (
    <div className="relative shrink-0">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="rounded-lg p-1.5 text-[var(--t-meta)] hover:bg-black/[0.04] hover:text-[var(--t-text)]"
        aria-label="Thread actions"
        data-testid={`blu-thread-menu-${thread.id}`}
      >
        <MoreHorizontal size={14} strokeWidth={1.5} />
      </button>
      {open ? (
        <div
          className="absolute right-0 top-full z-20 mt-1 min-w-[9.5rem] rounded-xl border border-[var(--t-border)]/50 bg-white p-1 shadow-md"
          onClick={(e) => e.stopPropagation()}
        >
          {renaming ? (
            <form
              className="p-1"
              onSubmit={(e) => {
                e.preventDefault();
                onRename?.(title);
                setRenaming(false);
                setOpen(false);
              }}
            >
              <input
                className="t-input !h-8 w-full !rounded-lg !px-2 text-[11px]"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                autoFocus
                data-testid="blu-thread-rename-input"
              />
            </form>
          ) : (
            <>
              <button
                type="button"
                className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-[11px] text-[var(--t-text)] hover:bg-black/[0.04]"
                onClick={() => {
                  setTitle(thread.title || "");
                  setRenaming(true);
                }}
              >
                <Pencil size={12} /> Rename
              </button>
              <button
                type="button"
                className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-[11px] text-[var(--t-text)] hover:bg-black/[0.04]"
                onClick={() => {
                  onTogglePin?.();
                  setOpen(false);
                }}
              >
                <Pin size={12} /> {thread.pinned ? "Unpin" : "Pin"}
              </button>
              <button
                type="button"
                className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-[11px] text-[var(--t-text)] hover:bg-black/[0.04]"
                onClick={() => {
                  onToggleArchive?.();
                  setOpen(false);
                }}
              >
                <Archive size={12} /> {thread.archived ? "Restore" : "Archive"}
              </button>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

export function isBluStep1Brief(content) {
  const text = String(content || "");
  // Options menus use ## SCOPE OPTIONS — do not treat as a single Step 1 brief.
  if (isBluScopeOptionsMessage(text) && !/(?:^|\n)\s*#{1,2}\s*SCOPE OF SUPPORT/i.test(text)) {
    return false;
  }
  return /(?:^|\n)\s*#{1,2}\s*SCOPE OF SUPPORT/i.test(text);
}

export function isBluScopeOptions(content) {
  return isBluScopeOptionsMessage(content);
}

export function hasBluFinalizeFence(content) {
  return /```\s*blu-finalize/i.test(String(content || ""));
}
